import { type ComponentProps } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';

export type V3AmbientButtonVariant = 'primary' | 'outline' | 'ghost';

const PRIMARY_BUTTON_CLASS =
    'inline-flex h-[38px] cursor-pointer items-center justify-center gap-2 ' +
    'rounded-full border border-transparent bg-[#5533EE] px-5 text-sm ' +
    'leading-5 font-semibold whitespace-nowrap text-white ' +
    'transition-[background-color,transform,opacity] duration-150 ' +
    'hover:bg-[#4629C7] active:scale-[0.96] ' +
    'disabled:pointer-events-none disabled:opacity-50';

const OUTLINE_BUTTON_CLASS =
    'inline-flex h-[38px] cursor-pointer items-center justify-center gap-2 ' +
    'rounded-full border border-[#DBE3E3] bg-white px-5 text-sm ' +
    'leading-5 font-semibold whitespace-nowrap text-[#002429] ' +
    'transition-[background-color,border-color,transform,opacity] duration-150 ' +
    'hover:border-[#BDC9C9] hover:bg-[#F0F5F5] active:scale-[0.96] ' +
    'disabled:pointer-events-none disabled:opacity-50';

const GHOST_BUTTON_CLASS =
    'inline-flex h-[38px] cursor-pointer items-center justify-center ' +
    'rounded-full px-3 text-sm leading-5 font-semibold whitespace-nowrap ' +
    'text-[#617578] transition-[background-color,color,transform,opacity] ' +
    'duration-150 hover:bg-[#F0F5F5] hover:text-[#002429] ' +
    'active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50';

const BUTTON_CLASSES: Record<V3AmbientButtonVariant, string> = {
    primary: PRIMARY_BUTTON_CLASS,
    outline: OUTLINE_BUTTON_CLASS,
    ghost: GHOST_BUTTON_CLASS,
};

export function V3AmbientButton({
    variant,
    loading = false,
    className,
    children,
    disabled,
    ...props
}: ComponentProps<'button'> & {
    readonly variant: V3AmbientButtonVariant;
    readonly loading?: boolean;
}) {
    return (
        <button
            type="button"
            className={cn(BUTTON_CLASSES[variant], className)}
            disabled={disabled || loading}
            aria-busy={loading}
            {...props}
        >
            {loading ? (
                <Spinner
                    size="sm"
                    className="shrink-0 motion-reduce:animate-none"
                />
            ) : null}
            {children}
        </button>
    );
}

export function V3SuggestedPrompts({
    prompts,
    disabled,
    onSelect,
}: {
    readonly prompts: ReadonlyArray<string>;
    readonly disabled: boolean;
    readonly onSelect: (prompt: string) => void;
}) {
    return (
        <div className="flex max-w-[760px] flex-wrap items-center justify-center gap-2.5">
            {prompts.map((prompt, index) => (
                <button
                    key={prompt}
                    type="button"
                    disabled={disabled}
                    className={cn(
                        'inline-flex cursor-pointer items-center justify-center rounded-full border font-semibold whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50',
                        index === 0
                            ? 'h-[30px] border-[#C9BFFA] bg-[#F3F0FE] px-3.5 text-[13px] leading-[18px] text-[#5533EE] hover:bg-[#EEEAFD]'
                            : 'h-9 border-[#DBE3E3] bg-white px-4 text-[13px] leading-[18px] font-normal text-[#244045] hover:bg-[#F0F5F5]',
                    )}
                    onClick={() => onSelect(prompt)}
                >
                    {prompt}
                </button>
            ))}
        </div>
    );
}

export function V3EmptyState({
    prompts,
    disabled,
    onPromptSelect,
}: {
    readonly prompts: ReadonlyArray<string>;
    readonly disabled: boolean;
    readonly onPromptSelect: (prompt: string) => void;
}) {
    return (
        <div className="flex min-h-64 flex-col items-center justify-center gap-5 px-4 py-8 text-center">
            <div className="flex flex-col items-center gap-2">
                <img
                    src="/brand/flowers/flower_1.png"
                    alt=""
                    className="size-24 object-contain"
                />
                <div className="pt-1.5 text-lg leading-[26px] font-semibold text-[#002429]">
                    Ready when you are
                </div>
                <p className="text-sm leading-5 text-[#617578]">
                    Ask about shifts, callouts, or caregivers
                </p>
            </div>
            <V3SuggestedPrompts
                prompts={prompts}
                disabled={disabled}
                onSelect={onPromptSelect}
            />
        </div>
    );
}
